import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, TrendingUp, AlertTriangle, XCircle } from 'lucide-react';
import { TestResults } from '../../types';
import { testData, getIconComponent } from '../../data/testData';

interface CategoryBreakdownProps {
  results: TestResults;
}

const CategoryBreakdown: React.FC<CategoryBreakdownProps> = ({ results }) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const getStatusIcon = (status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    switch(status) {
      case 'Ready': return <TrendingUp className="w-5 h-5 text-success-600" />;
      case 'Almost Ready': return <AlertTriangle className="w-5 h-5 text-warning-600" />;
      case 'Not Ready': return <XCircle className="w-5 h-5 text-error-600" />;
    }
  };

  const getStatusColor = (status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    switch(status) {
      case 'Ready': return 'bg-success-100 text-success-700 border-success-200';
      case 'Almost Ready': return 'bg-warning-100 text-warning-700 border-warning-200';
      case 'Not Ready': return 'bg-error-100 text-error-700 border-error-200';
    }
  };

  const getRecommendations = (categoryId: string, status: 'Ready' | 'Almost Ready' | 'Not Ready') => {
    const baseRecommendations: Record<string, string[]> = {
      'business-plan': [
        'Develop a comprehensive export business plan with clear objectives',
        'Establish dedicated export team or assign responsibilities',
        'Create detailed market entry strategies for target countries'
      ],
      'finance': [
        'Secure adequate funding for export operations',
        'Implement foreign exchange risk management strategies',
        'Explore government export financing programs'
      ],
      'documentation': [
        'Standardize export documentation processes',
        'Train staff on international shipping requirements',
        'Implement digital document management systems'
      ],
      'product': [
        'Ensure products meet international standards and certifications',
        'Adapt packaging for international shipping requirements',
        'Conduct market testing in target countries'
      ],
      'compliance': [
        'Develop comprehensive compliance policies and procedures',
        'Stay updated on export regulations and requirements',
        'Implement screening processes for international partners'
      ],
      'market': [
        'Conduct thorough market research for target countries',
        'Build relationships with local partners and distributors',
        'Develop market-specific marketing strategies'
      ],
      'digital': [
        'Optimize website for international audiences',
        'Implement e-commerce capabilities for global markets',
        'Develop digital marketing strategies for target markets'
      ],
      'logistics': [
        'Establish relationships with reliable freight forwarders',
        'Develop efficient international shipping processes',
        'Implement shipment tracking and insurance policies'
      ],
      'legal': [
        'Consult with international trade law experts',
        'Protect intellectual property in target markets',
        'Develop standard international contracts and agreements'
      ],
      'risk': [
        'Implement comprehensive risk assessment procedures',
        'Develop crisis management and contingency plans',
        'Consider export credit insurance options'
      ]
    };

    return baseRecommendations[categoryId] || [
      'Seek professional guidance for this category',
      'Attend relevant training programs',
      'Connect with industry experts and mentors'
    ];
  };

  // Sort categories by score (lowest first for priority)
  const sortedCategories = [...results.categories].sort((a, b) => a.percentage - b.percentage);

  return (
    <motion.div
      className="mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Category Breakdown</h2>
        <div className="text-sm text-gray-600">
          Click on any category to see detailed analysis
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedCategories.map((categoryResult, index) => {
          const categoryInfo = testData.find(cat => cat.id === categoryResult.categoryId);
          if (!categoryInfo) return null;
          
          const IconComponent = getIconComponent(categoryInfo.icon);
          const isExpanded = expandedCategories.has(categoryResult.categoryId);
          
          return (
            <motion.div
              key={categoryResult.categoryId}
              className="bg-white rounded-xl shadow-card overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
              whileHover={{ y: -4 }}
              onClick={() => toggleCategory(categoryResult.categoryId)}
            >
              {/* Collapsed View */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary-100 p-2 rounded-lg">
                      <IconComponent className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800 text-sm">{categoryInfo.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {getStatusIcon(categoryResult.status)}
                        <span className="text-xs font-medium text-gray-600">
                          {categoryResult.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-2xl font-bold text-primary-600">
                      {Math.round(categoryResult.percentage)}%
                    </div>
                    <motion.div
                      animate={{ rotate: isExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </motion.div>
                  </div>
                </div>
                
                <div className="w-full bg-gray-200 h-2 rounded-full mb-3">
                  <motion.div 
                    className="bg-primary-600 h-full rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${categoryResult.percentage}%` }}
                    transition={{ delay: index * 0.1 + 0.3, duration: 0.8, ease: "easeOut" }}
                  />
                </div>
                
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(categoryResult.status)}`}>
                  {categoryResult.score} / {categoryResult.totalPossible} points
                </div>
              </div>
              
              {/* Expanded View */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="border-t border-gray-100"
                  >
                    <div className="p-6 bg-gray-50">
                      <h4 className="font-semibold text-gray-800 mb-3">Analysis & Recommendations</h4>
                      
                      <p className="text-sm text-gray-600 mb-4">
                        {categoryInfo.description}
                      </p>
                      
                      <div className="space-y-3">
                        <h5 className="font-medium text-gray-700 text-sm">Priority Actions:</h5>
                        <ul className="space-y-2">
                          {getRecommendations(categoryResult.categoryId, categoryResult.status).map((rec, i) => (
                            <motion.li
                              key={i}
                              className="flex items-start gap-2 text-sm text-gray-600"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.1 }}
                            >
                              <ChevronRight className="w-3 h-3 text-primary-500 mt-0.5 flex-shrink-0" />
                              <span>{rec}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default CategoryBreakdown;